import { Encounter, Patient } from '@medplum/fhirtypes'
import { ChartItem } from './PatientChart.utils.tsx'
import { Button } from '@mantine/core'
import { IconPlus } from '@tabler/icons-react'
import { ResourceTable } from '@/pages/Patient/components/ResourceTable.tsx'
import { formatDateTime } from '@/utils.ts'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useScrollContext } from '@/context/ScrollContext'
import { useMedplum } from '@medplum/react'
import { useQuery } from '@tanstack/react-query'
import { SELF_PAY_COVERAGE_REFERENCE, type SelfPayStatus } from '@/model/middleware-types'
import { readSelfPayStatus } from '@/utils/selfPay'

type Props = {
  patient: Patient
  item: ChartItem
}

const availFields = [
  { code: 'type', title: 'Type', width: '30%' },
  { code: 'date', title: 'Date', width: '130px' },
  { code: 'status', title: 'Status' },
  { code: 'payment', title: 'Payment' },
  { code: 'performer', title: 'Performer' },
  { code: 'location', title: 'Location' },
  { code: 'indication', title: 'Indication' },
  { code: 'action', title: 'Call link' },
]

/** appointment id → self-pay status, for the patient's self-pay visits only (one search per chart). */
function useSelfPayStatuses(patientId: string | undefined) {
  const medplum = useMedplum()

  return useQuery({
    queryKey: ['self-pay-statuses', patientId],
    enabled: Boolean(patientId),
    queryFn: async () => {
      const appointments = await medplum.searchResources('Appointment', {
        patient: `Patient/${patientId}`,
        'supporting-info': SELF_PAY_COVERAGE_REFERENCE,
        _count: '100',
      })

      return new Map(
        appointments.map((appointment) => [appointment.id, readSelfPayStatus(appointment)]),
      )
    },
  })
}

const renderColumn =
  (
    navigate: (link: string) => void,
    selfPayStatuses: Map<string | undefined, SelfPayStatus | null>,
  ) =>
  (resource: Encounter, itemCode: string) => {
    switch (itemCode) {
      case 'type': {
        const types: string[] = []

        if (resource.class.display && resource.class.display.toLowerCase() !== 'unknown') {
          const type =
            resource.class.display.charAt(0).toUpperCase() + resource.class.display.slice(1)
          types.push(type)
        }

        for (const t of resource.type || []) {
          if (t.text && !types.includes(t.text)) types.push(t.text)
        }
        return types.join('\n') || ''
      }
      case 'date':
        return resource.period?.start ? formatDateTime(resource.period.start) : ''
      case 'status':
        return resource.status || ''
      case 'payment': {
        const appointmentId = resource.appointment?.[0]?.reference?.split('/')[1]
        const status = appointmentId ? selfPayStatuses.get(appointmentId) : undefined
        if (status === 'unpaid') {
          return (
            <span className="rounded-md bg-[#FDECE8] px-2 py-[3px] text-[11px] font-bold text-[#E5533D]">
              Unpaid
            </span>
          )
        }
        if (status === 'paid') return 'Self-pay paid'
        if (status === 'refunded') return 'Self-pay refunded'
        return ''
      }
      case 'performer': {
        let performer = ''
        const performers = []
        const ext = resource.extension?.find((e) => e.url?.match(/record-recorder/))

        if (ext?.valueReference?.display) {
          performer = ext.valueReference.display
          performers.push(ext.valueReference.display)
        }

        const primaryParticipant = resource.participant?.find((p) =>
          p.type?.find((t) => t.text === 'primary performer'),
        )

        if (primaryParticipant?.individual?.display) {
          if (!performers.includes(primaryParticipant.individual.display)) {
            performers.push(primaryParticipant.individual.display)
          }
        } else {
          const participant = resource.participant?.find((p) => p.individual?.display)
          if (participant?.individual?.display) {
            if (!performers.includes(participant.individual.display)) {
              performer += `\n${participant.individual.display}`
              performers.push(participant.individual.display)
            }
          }
        }

        const provenances =
          resource.contained?.filter((item) => item.resourceType === 'Provenance') || []

        for (const provenance of provenances) {
          const agent = provenance.agent?.find((a) =>
            a.type?.coding?.find((t) => t.code === 'source'),
          )

          if (agent?.who?.display && !performers.includes(agent?.who?.display)) {
            performer += `\n${agent.who.display}`
            performers.push(agent.who.display)
          }
        }

        return performer
      }
      case 'location': {
        const location = resource.contained?.filter((i) => i.resourceType === 'Location') || []

        let locationDisplay = ''

        for (const loc of location) {
          locationDisplay = loc.name || 'Unknown Location'

          if (loc.address?.text) {
            locationDisplay += `\n${loc.address.text}`
          }
        }
        return locationDisplay || resource.location?.[0]?.location?.display || ''
      }
      case 'indication':
        return resource.reasonCode?.[0]?.coding?.[0]?.display || ''

      case 'action': {
        const savedSessionName = resource.extension?.find(
          (extension) =>
            extension.url === 'http://example.com/fhir/StructureDefinition/teleconference-session',
        )?.valueString
        return (
          <Button
            variant="outline"
            disabled={!savedSessionName}
            onClick={() => navigate(`/call/${resource.id}`)}
          >
            go to call
          </Button>
        )
      }
      default:
        return ''
    }
  }

export const Encounters = ({ patient, item }: Props) => {
  const navigate = useNavigate()
  const { scrollToTop } = useScrollContext()

  const { data: selfPayStatuses } = useSelfPayStatuses(patient.id)

  const boundRenderColumn = useMemo(
    () => renderColumn((link) => void navigate(link), selfPayStatuses ?? new Map()),
    [navigate, selfPayStatuses],
  )

  useEffect(() => {
    scrollToTop()
  }, [])

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center gap-4">
        <p className="text-2xl font-bold">Encounters</p>
        <Button
          variant="outline"
          className="!text-primary !border-[#D0E1F2] !bg-gradient-to-b from-[#F0F7FF] to-[#FFFFFF] !p-2 hover:from-[#FFFFFF]"
        >
          Add <IconPlus width={15} className="ml-1" />
        </Button>
      </div>

      <ResourceTable
        patient={patient}
        item={item}
        availFields={availFields}
        renderColumn={boundRenderColumn}
        defaultSort="date"
        includeProvenance
        blockRemove
      />
    </div>
  )
}
